import React, { useEffect, useId, useRef, useState } from 'react';

const normalize = text => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Single-choice filter dropdown: an "all" row plus options with result counts.
function FilterPicker({ label, icon, allLabel, options, value, onChange, searchPlaceholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const searchable = options.length > 6;
  const rows = [{ value: '', label: allLabel }, ...options]
    .filter(row => !query || row.value === '' || normalize(row.label).includes(normalize(query)));
  const selected = options.find(option => option.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(Math.max(0, rows.findIndex(row => row.value === value)));
    (searchable ? searchRef.current : listRef.current)?.focus();
    // Only when opening; typing in the search resets the highlight separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const close = () => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  const choose = (row) => {
    onChange(row.value);
    close();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(rows.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(0, index - 1));
    } else if (event.key === 'Enter' && rows[activeIndex]) {
      event.preventDefault();
      choose(rows[activeIndex]);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      close();
    }
  };

  return (
    <div className="filter-picker" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`filter-picker-trigger${selected ? ' has-value' : ''}${open ? ' open' : ''}`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(event) => {
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {icon ? <i className={`fas ${icon} filter-picker-icon`} aria-hidden="true" /> : null}
        <span className="filter-picker-value">{selected ? selected.label : allLabel}</span>
        <i className="fas fa-chevron-down filter-picker-chevron" aria-hidden="true" />
      </button>

      {open ? (
        <div className="filter-picker-popover">
          {searchable ? (
            <div className="filter-picker-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={`${listId}-${activeIndex}`}
              />
            </div>
          ) : null}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label}
            tabIndex={-1}
            className="filter-picker-list"
            onKeyDown={onKeyDown}
            aria-activedescendant={`${listId}-${activeIndex}`}
          >
            {rows.map((row, index) => {
              const isSelected = row.value === value;
              return (
                <li
                  key={row.value || 'all'}
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  className={`filter-picker-option${index === activeIndex ? ' active' : ''}${isSelected ? ' selected' : ''}${row.value === '' ? ' all' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(row)}
                >
                  <i className={`fas fa-check filter-picker-check${isSelected ? '' : ' hidden'}`} aria-hidden="true" />
                  <span className="filter-picker-option-label">{row.label}</span>
                  {row.count !== undefined ? <span className="filter-picker-count">{row.count}</span> : null}
                </li>
              );
            })}
            {rows.length === 1 && query ? <li className="filter-picker-empty">Cap resultat</li> : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default FilterPicker;
